import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { chatWithRodman, getRodmanPicks, getRodmanStats } from './api';
import { setRodmanFavicon, setDefaultFavicon } from './utils';
import ReactMarkdown from 'react-markdown';
import rodmanLogo from './rodman_logo.jpg';
import './Rodman.css';

// Import animation components
import { ThinkingIndicator, TypewriterText } from './components/RodmanAnimation';

// Enhanced Message Component with Claude-style animations
const AnimatedRodmanMessage = ({ message, isLatest, onComplete }) => {
    const [showContent, setShowContent] = useState(false);
    const [isTyping, setIsTyping] = useState(false);
    const messageRef = useRef(null);
    const hasScrolledRef = useRef(false);

    // Handle scroll positioning
    useEffect(() => {
        if (isLatest && messageRef.current && !hasScrolledRef.current) {
            hasScrolledRef.current = true;
            
            // Scroll to top of new message for better reading experience
            requestAnimationFrame(() => {
                messageRef.current.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                    inline: 'nearest'
                });
            });
        }
    }, [isLatest]);

    // Animation sequence for Rodman messages
    useEffect(() => {
        if (message.type === 'rodman' && isLatest) {
            // Show thinking for 1.5 seconds
            const thinkingTimer = setTimeout(() => {
                setShowContent(true);
                setIsTyping(true);
            }, 1500);

            return () => clearTimeout(thinkingTimer);
        } else {
            setShowContent(true);
        }
    }, [message.type, isLatest]);

    const handleTypewriterComplete = useCallback(() => {
        setIsTyping(false);
        if (onComplete) {
            onComplete();
        }
    }, [onComplete]);

    return (
        <div ref={messageRef} className={`message ${message.type}`}>
            <div className={`message-content ${message.isSuggestions ? 'isSuggestions' : ''}`}>
                {message.type === 'rodman' && isLatest && !showContent ? (
                    <ThinkingIndicator text="Analyzing" />
                ) : (
                    <>
                        <div className="message-text">
                            {message.type === 'rodman' && isTyping && isLatest ? (
                                <TypewriterText
                                    text={message.content}
                                    speed={20}
                                    onComplete={handleTypewriterComplete}
                                />
                            ) : (
                                <ReactMarkdown 
                                    components={{
                                        p: ({ children }) => <span>{children}</span>,
                                        strong: ({ children }) => <strong style={{ fontWeight: '700', color: '#007AFF', textShadow: '0 1px 2px rgba(0,0,0,0.1)' }}>{children}</strong>,
                                        em: ({ children }) => <em style={{ fontStyle: 'italic', color: '#007AFF', opacity: '0.9' }}>{children}</em>
                                    }}
                                >
                                    {message.content}
                                </ReactMarkdown>
                            )}
                        </div>
                        
                        {message.suggestionButtons && (
                            <div className="suggestion-buttons">
                                {message.suggestionButtons.map((suggestion, index) => (
                                    <button
                                        key={index}
                                        className="suggestion-btn"
                                        onClick={() => message.onSuggestionClick && message.onSuggestionClick(suggestion)}
                                    >
                                        {suggestion}
                                    </button>
                                ))}
                            </div>
                        )}
                        
                        {message.gameContext && (
                            <div className="game-context-tag">
                                📊 {message.gameContext.game}
                            </div>
                        )}
                        
                        {message.confidence && (
                            <div 
                                className="confidence-indicator"
                                style={{ color: formatConfidence(message.confidence).color }}
                            >
                                🎯 {formatConfidence(message.confidence).text}
                            </div>
                        )}
                        
                        <div className="message-time">
                            {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

const formatConfidence = (confidence) => {
    if (!confidence) return null;
    const percentage = Math.round(confidence * 100);
    
    if (percentage >= 75) {
        return { level: 'high', text: `${percentage}% confident`, color: '#34C759' };
    } else if (percentage >= 60) {
        return { level: 'medium', text: `${percentage}% confident`, color: '#FF9F0A' };
    } else {
        return { level: 'low', text: `${percentage}% confident`, color: '#8E8E93' };
    }
};

// Enhanced Rodman component with Claude-style animations
const RodmanEnhanced = () => {
    const [messages, setMessages] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isThinking, setIsThinking] = useState(false);
    const [todaysPicks, setTodaysPicks] = useState([]);
    const [stats, setStats] = useState(null);
    const [currentSport, setCurrentSport] = useState('general');
    const [hasAutoQueried, setHasAutoQueried] = useState(false);
    const [hasGamesTonight, setHasGamesTonight] = useState(false);
    const hasProcessedURL = useRef(false);
    const chatContainerRef = useRef(null);
    const location = useLocation();
    const navigate = useNavigate();

    // Disable browser auto-scroll anchoring
    useEffect(() => {
        if (chatContainerRef.current) {
            chatContainerRef.current.style.overflowAnchor = 'none';
        }
    }, []);

    // Handle URL parameters and state for direct game analysis
    useEffect(() => {
        if (hasProcessedURL.current) return;
        hasProcessedURL.current = true;
        
        const gameContext = location.state?.gameContext;
        const standingsContext = location.state?.standingsContext;
        const gameString = location.state?.gameString;
        const league = location.state?.league;
        const presetQuery = location.state?.presetQuery;
        const autoStart = location.state?.autoStart;
        
        const urlParams = new URLSearchParams(location.search);
        const urlGame = urlParams.get('game');
        const urlLeague = urlParams.get('league');
        
        if (autoStart && presetQuery) {
            let welcomeMessage;
            let contextData;
            
            if (gameContext) {
                welcomeMessage = `Alright, ${gameString}. Let me break this down for you.`;
                contextData = gameContext;
            } else if (standingsContext) {
                welcomeMessage = `Looking at the ${league} standings. Here's what I see...`;
                contextData = { standings: standingsContext, league };
            } else {
                welcomeMessage = `Let me analyze this for you.`;
                contextData = { league };
            }
            
            setMessages([{
                id: Date.now(),
                type: 'rodman',
                content: welcomeMessage,
                timestamp: new Date(),
                sport: league?.toLowerCase() === 'nba' ? 'nba' : 'premier_league'
            }]);
            
            sendMessage(presetQuery, contextData);
            navigate('/rodman', { replace: true });
        } else if (gameContext && gameString && league) {
            const welcomeMessage = `Alright, ${gameString}. Let me break this down for you.`;
            setMessages([{
                id: Date.now(),
                type: 'rodman',
                content: welcomeMessage,
                timestamp: new Date(),
                sport: league.toLowerCase() === 'nba' ? 'nba' : 'premier_league'
            }]);
            
            const query = `Tell me about ${gameString} - who wins and why?`;
            sendMessage(query, gameContext);
            navigate('/rodman', { replace: true });
        } else if (urlGame && urlLeague) {
            const welcomeMessage = `Alright, ${urlGame}. Let me break this down for you.`;
            setMessages([{
                id: Date.now(),
                type: 'rodman',
                content: welcomeMessage,
                timestamp: new Date(),
                sport: urlLeague.toLowerCase() === 'nba' ? 'nba' : 'premier_league'
            }]);
            
            const query = `Tell me about ${urlGame} - who wins and why?`;
            sendMessage(query, { game: urlGame, league: urlLeague });
            navigate('/rodman', { replace: true });
        } else {
            loadSmartSuggestions();
        }
        
        loadInitialData();
    }, []);

    // Handle favicon
    useEffect(() => {
        setRodmanFavicon();
        return () => {
            setDefaultFavicon();
        };
    }, []);

    const checkForGamesTonight = async () => {
        try {
            const today = new Date().toISOString().split('T')[0];
            const response = await fetch(`http://localhost:8005/api/v1/go/games?date=${today}`);
            
            if (response.ok) {
                const games = await response.json();
                return Array.isArray(games) && games.length > 0;
            }
            return false;
        } catch (error) {
            console.error('Error checking for games tonight:', error);
            return false;
        }
    };

    const loadInitialData = async () => {
        try {
            const [picksResponse, statsResponse, gamesCheck] = await Promise.allSettled([
                getRodmanPicks(),
                getRodmanStats(),
                checkForGamesTonight()
            ]);

            if (picksResponse.status === 'fulfilled') {
                setTodaysPicks(picksResponse.value.picks || []);
            }

            if (statsResponse.status === 'fulfilled') {
                setStats(statsResponse.value);
            }

            if (gamesCheck.status === 'fulfilled') {
                setHasGamesTonight(gamesCheck.value);
            }
        } catch (error) {
            console.error('Error loading initial data:', error);
        }
    };

    const sendMessage = async (messageText, gameContext = null, isPreset = false) => {
        if (!messageText.trim() || isLoading) return;

        if (messages.length > 0 && messages[messages.length - 1].content === messageText && messages[messages.length - 1].type === 'user') {
            return;
        }

        const userMessage = {
            id: Date.now(),
            type: 'user',
            content: messageText,
            timestamp: new Date()
        };

        setMessages(prev => [...prev, userMessage]);
        setIsLoading(true);
        setIsThinking(true);

        try {
            // Handle both old gameContext and new query_type context
            let actualGameContext = gameContext;
            let queryType = 'general';
            
            if (gameContext && gameContext.query_type) {
                queryType = gameContext.query_type;
                actualGameContext = gameContext.gameContext || null;
            }

            const response = await chatWithRodman(messageText, actualGameContext, isPreset, queryType);
            
            const rodmanMessage = {
                id: Date.now() + 1,
                type: 'rodman',
                content: response.response,
                timestamp: new Date(),
                confidence: response.confidence,
                sport: response.sport_detected,
                gameContext: actualGameContext,
                queryType: queryType
            };

            setMessages(prev => [...prev, rodmanMessage]);
            setCurrentSport(response.sport_detected);
            await loadSmartSuggestions(true);
            
        } catch (error) {
            console.error('Error chatting with Rodman:', error);
            
            const errorMessage = {
                id: Date.now() + 1,
                type: 'rodman',
                content: "System hiccup. My accuracy stays money though - try again.",
                timestamp: new Date(),
                sport: 'general',
                isError: true
            };

            setMessages(prev => [...prev, errorMessage]);
            await loadSmartSuggestions(true);
        } finally {
            setIsLoading(false);
            setIsThinking(false);
        }
    };

    const getQuickSuggestions = async () => {
        const now = new Date();
        const hour = now.getHours();
        const dayOfWeek = now.getDay();
        
        const hasGamesToday = await checkForGamesTonight();
        
        if (hasGamesToday) {
            if (hour >= 18) {
                return [
                    "What are your highest confidence picks tonight?",
                    "Analyze tonight's NBA and Premier League matchups",
                    "Show me tonight's upset potential",
                    "Which teams are your locks tonight?"
                ];
            } else {
                return [
                    "What games should I watch today?",
                    "Show me today's NBA and Premier League predictions",
                    "Which teams are your highest confidence today?",
                    "What's your analysis for today's matchups?"
                ];
            }
        } else {
            if (dayOfWeek >= 4 && dayOfWeek <= 6) {
                return [
                    "What should I expect this weekend?",
                    "Show me your week's performance so far",
                    "Which teams are trending up in power rankings?",
                    "Any weekend upset alerts brewing?"
                ];
            } else if (dayOfWeek === 0 || dayOfWeek === 1) {
                return [
                    "How did your predictions perform this week?",
                    "Show me the week's biggest power ranking movers",
                    "What teams exceeded expectations this week?",
                    "Looking ahead - what's the week's storyline?"
                ];
            } else {
                return [
                    "How are you performing this week?",
                    "Show me recent NBA and Premier League form",
                    "Which teams are surprising you this season?",
                    "What's your confidence level on recent picks?"
                ];
            }
        }
    };

    const loadSmartSuggestions = async (append = false) => {
        try {
            const suggestions = await getQuickSuggestions();

            const suggestionMessage = {
                id: Date.now(),
                type: 'rodman',
                content: "Try asking me about:",
                timestamp: new Date(),
                sport: 'general',
                isSuggestions: true,
                suggestionButtons: suggestions,
                onSuggestionClick: (suggestion) => sendMessage(suggestion)
            };

            setMessages(prev => (append ? [...prev, suggestionMessage] : [suggestionMessage]));
        } catch (error) {
            console.error('Error loading smart suggestions:', error);
        }
    };

    const getSmartButtonText = (buttonType) => {
        if (!stats) {
            const fallbacks = {
                'tonight_winners': '🎯 Tonight\'s winners',
                'hot_streak': '🔥 Hot picks',
                'surprise_picks': '⚡ Surprise picks',
                'championship_race': '🏆 Championship Race',
                'weekend_winners': '🎯 Weekend winners',
                'week_surprises': '⚡ Surprises',
            };
            return fallbacks[buttonType] || '🎯 Analyze';
        }

        const accuracy = stats.accuracy || '';
        const seasonRecord = stats.season_record || '';
        const accuracyMatch = accuracy.match(/(\d+(?:\.\d+)?)%/);
        const accuracyPct = accuracyMatch ? parseFloat(accuracyMatch[1]) : 0;
        
        const seasonMatch = seasonRecord.match(/(\d+)-(\d+)/);
        const seasonWins = seasonMatch ? parseInt(seasonMatch[1]) : 0;
        const seasonLosses = seasonMatch ? parseInt(seasonMatch[2]) : 0;
        const seasonTotal = seasonWins + seasonLosses;
        const seasonPct = seasonTotal > 0 ? Math.round((seasonWins / seasonTotal) * 100) : 0;
        
        const isHot = accuracyPct >= 75;
        const isElite = seasonPct >= 70;

        switch (buttonType) {
            case 'tonight_winners':
                return isHot ? '🔥 Hot picks tonight' : '🎯 Tonight\'s winners';
            case 'hot_streak':
                return isElite ? '🔥 Elite hot streak' : '🔥 Hot streak picks';
            case 'surprise_picks':
                return isElite ? '⚡ Elite upset radar' : '⚡ Surprise picks';
            case 'championship_race':
                return '🏆 Championship Race';
            case 'weekend_winners':
                return isHot ? '🔥 Hot weekend picks' : '🎯 Weekend winners';
            case 'week_surprises':
                return isElite ? '⚡ Elite upset radar' : '⚡ Wild cards';
            default:
                return '🎯 Analyze';
        }
    };

    return (
        <div className="rodman-container">
            <div className="rodman-header">
                <div className="rodman-avatar">
                    <img src={rodmanLogo} alt="Rodman" className="avatar-image" />
                </div>
                <div className="rodman-info">
                    <h1 className="rodman-title">RODMAN AI</h1>
                    <p className="rodman-subtitle">
                        {stats?.full_description || ''}
                    </p>
                </div>
            </div>

            <div ref={chatContainerRef} className="chat-messages">
                {messages.map((message, index) => (
                    <AnimatedRodmanMessage
                        key={message.id}
                        message={message}
                        isLatest={index === messages.length - 1}
                        onComplete={() => {}}
                    />
                ))}
                
                {isThinking && (
                    <div className="message rodman">
                        <div className="message-content">
                            <ThinkingIndicator text="Analyzing" />
                        </div>
                    </div>
                )}
            </div>

            <div className="quick-actions">
                {hasGamesTonight ? (
                    <>
                        <button 
                            className="quick-action-btn"
                            onClick={() => sendMessage("Who wins tonight? Show me your highest confidence picks.", null, true)}
                        >
                            {getSmartButtonText('tonight_winners')}
                        </button>
                        <button 
                            className="quick-action-btn"
                            onClick={() => sendMessage("Show me your hottest predictions right now. What's your hot streak?", null, true)}
                        >
                            {getSmartButtonText('hot_streak')}
                        </button>
                        <button 
                            className="quick-action-btn"
                            onClick={() => sendMessage("Any surprise picks tonight? Show me the upset potential.", null, true)}
                        >
                            {getSmartButtonText('surprise_picks')}
                        </button>
                        <button 
                            className="quick-action-btn"
                            onClick={() => sendMessage("Who's winning the championship race? Show me the title contenders and their chances.", null, true)}
                        >
                            {getSmartButtonText('championship_race')}
                        </button>
                    </>
                ) : (
                    <>
                        <button 
                            className="quick-action-btn"
                            onClick={() => sendMessage("Who wins this weekend? Show me your best picks.", null, true)}
                        >
                            {getSmartButtonText('weekend_winners')}
                        </button>
                        <button 
                            className="quick-action-btn"
                            onClick={() => sendMessage("Show me your hottest predictions this week. What's your hot streak?", null, true)}
                        >
                            {getSmartButtonText('hot_streak')}
                        </button>
                        <button 
                            className="quick-action-btn"
                            onClick={() => sendMessage("Any surprise picks this week? Show me the upset potential.", null, true)}
                        >
                            {getSmartButtonText('week_surprises')}
                        </button>
                        <button 
                            className="quick-action-btn"
                            onClick={() => sendMessage("Who's winning the championship race? Show me the title contenders and their chances.", null, true)}
                        >
                            {getSmartButtonText('championship_race')}
                        </button>
                    </>
                )}
            </div>

            <div className="context-actions">
                {hasGamesTonight ? (
                    <>
                        <button 
                            className="quick-action-btn"
                            onClick={() => sendMessage("Show me your highest confidence locks today", { query_type: "confidence_locks" }, true)}
                        >
                            🔥 Today's locks
                        </button>
                        <button 
                            className="quick-action-btn"
                            onClick={() => sendMessage("Any upset alerts brewing today?", { query_type: "upset_alerts" }, true)}
                        >
                            ⚡ Upset alerts
                        </button>
                        <button 
                            className="quick-action-btn"
                            onClick={() => sendMessage("What's your hottest streak right now?", { query_type: "hot_streak" }, true)}
                        >
                            🔥 Hot streak
                        </button>
                        <button 
                            className="quick-action-btn"
                            onClick={() => sendMessage("Show me where your model is most accurate", { query_type: "accuracy_showcase" }, true)}
                        >
                            🎯 Accuracy showcase
                        </button>
                    </>
                ) : (
                    <>
                        <button 
                            className="quick-action-btn"
                            onClick={() => sendMessage("How did your predictions perform this week?", { query_type: "weekly_performance" }, true)}
                        >
                            📊 This week's performance
                        </button>
                        <button 
                            className="quick-action-btn"
                            onClick={() => sendMessage("Which teams are trending up in power rankings?", { query_type: "power_movers" }, true)}
                        >
                            📈 Power movers
                        </button>
                        <button 
                            className="quick-action-btn"
                            onClick={() => sendMessage("Show me your best accuracy spots", { query_type: "accuracy_showcase" }, true)}
                        >
                            🎯 Best accuracy spots
                        </button>
                        <button 
                            className="quick-action-btn"
                            onClick={() => sendMessage("What's the week's biggest storylines?", { query_type: "week_narrative" }, true)}
                        >
                            📰 Week's storylines
                        </button>
                    </>
                )}
            </div>
        </div>
    );
};

export default RodmanEnhanced;